/**
 * Shared Updater Services Hook
 *
 * Provides shared service instances across all wizard steps.
 * This ensures the device connection, logger, and loader services
 * persist and are accessible throughout the update flow.
 */

import * as React from 'react';
import { UpdaterLoggerService } from '../services/updater-logger.service';
import { IMXLoaderService } from '../services/imx-loader.service';
import { DeviceConnectionManager } from '../services/device-connection-manager.service';
import { FirmwareDownloadService } from '../services/firmware-download.service';
import { DeviceConfig, DeviceConfigChain, IHIDDevice, IHIDNavigator, ProgressCallback } from '../types/imx-types';
import { FirmwareVersion } from '../types/firmware-types';
import { DeviceInfo } from '../types/wizard-types';

// Device configuration chains for multi-stage boot
// Format: [vid, pid, configFile][]
const DEVICE_CONFIG_CHAINS: DeviceConfigChain[] = [
  // i.MX 6ULL: ROM -> U-Boot
  [
    [0x15a2, 0x0080, 'mx6ull_usb_rom'],
    [0x1b67, 0x4000, 'mx6ull_usb_sdp_uboot'],
  ],
  // i.MX6 variants
  [
    [0x15a2, 0x0054, 'mx6_usb_rom'],
    [0x1b67, 0x4fff, 'mx6_usb_sdp_spl'],
    [0x1b67, 0x4000, 'mx6_usb_sdp_uboot'],
  ],
  [
    [0x15a2, 0x0061, 'mx6_usb_rom'],
    [0x1b67, 0x4fff, 'mx6_usb_sdp_spl'],
    [0x1b67, 0x4000, 'mx6_usb_sdp_uboot'],
  ],
  // i.MX7
  [
    [0x15a2, 0x0076, 'mx7_usb_rom'],
    [0x1b67, 0x4000, 'mx7_usb_sdp_uboot'],
  ],
];

// Device configurations parsed from .conf files
const DEVICE_CONFIGS: Record<string, DeviceConfig> = {
  mx6ull_usb_rom: {
    name: 'mx6ull',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x80000000, size: 0x40000000 },
      { start: 0x00900000, size: 0x20000 },
    ],
    work: [
      { filename: 'u-boot.imx', dcd: true, jump: 'header' },
    ],
  },
  mx6ull_usb_sdp_uboot: {
    name: 'mx6ull_usb_sdp_uboot',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x80000000, size: 0x40000000 },
      { start: 0x00900000, size: 0x20000 },
    ],
    work: [
      { filename: 'hawkin_recovery.itb', loadAddr: 0x82100000 },
      { filename: 'boot-sdp.scr', loadAddr: 0x82000000, jump: true, jumpAddr: 0x82000000 },
    ],
  },
  mx6_usb_rom: {
    name: 'mx6',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x10000000, size: 0x80000000 },
      { start: 0x00907000, size: 0x31000 },
    ],
    work: [
      { filename: 'SPL', dcd: true, jump: 'header' },
    ],
  },
  mx6_usb_sdp_spl: {
    name: 'mx6_usb_sdp_spl',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x10000000, size: 0x80000000 },
      { start: 0x00907000, size: 0x31000 },
    ],
    work: [
      { filename: 'u-boot.imx', jump: 'header' },
    ],
  },
  mx6_usb_sdp_uboot: {
    name: 'mx6_usb_sdp_uboot',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x10000000, size: 0x80000000 },
      { start: 0x00907000, size: 0x31000 },
    ],
    work: [
      { filename: 'hawkin_recovery.itb', loadAddr: 0x12100000 },
      { filename: 'boot-sdp.scr', loadAddr: 0x12000000, jump: true, jumpAddr: 0x12000000 },
    ],
  },
  mx7_usb_rom: {
    name: 'mx7',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x80000000, size: 0x20000000 },
      { start: 0x00900000, size: 0x20000 },
    ],
    work: [
      { filename: 'u-boot.imx', dcd: true, jump: 'header' },
    ],
  },
  mx7_usb_sdp_uboot: {
    name: 'mx7_usb_sdp_uboot',
    mode: 'HID',
    headerType: 'MX53',
    maxTransfer: 1024,
    dcdAddr: 0x910000,
    ramAreas: [
      { start: 0x80000000, size: 0x20000000 },
      { start: 0x00900000, size: 0x20000 },
    ],
    work: [
      { filename: 'hawkin_recovery.itb', loadAddr: 0x82100000 },
      { filename: 'boot-sdp.scr', loadAddr: 0x82000000, jump: true, jumpAddr: 0x82000000 },
    ],
  },
};

/**
 * File loader type for loading binary files
 */
export type FileLoader = (filename: string) => Promise<Uint8Array>;

/**
 * Context value for updater services
 */
export interface UpdaterServicesContextValue {
  /** Logger service for session logging */
  logger: UpdaterLoggerService;
  /** IMX loader service for file transfers */
  imxLoader: IMXLoaderService;
  /** Device connection manager */
  connectionManager: DeviceConnectionManager;
  /** Firmware download service */
  firmwareService: FirmwareDownloadService;
  /** Current HID device (if connected) */
  hidDevice: HIDDevice | null;
  /** Set the current HID device */
  setHidDevice: (device: HIDDevice | null) => void;
  /** Load binary files for transfer */
  loadFile: FileLoader;
  /** Update progress callback */
  setProgressCallback: (callback: ProgressCallback | undefined) => void;
  /** Current firmware info (after fetching) */
  firmware: FirmwareVersion | null;
  /** Fetch firmware from cloud storage */
  fetchFirmware: (version?: string) => Promise<FirmwareVersion>;
  /** Disconnect and cleanup the HID device */
  disconnectDevice: () => Promise<void>;
}

/**
 * React context for updater services
 */
const UpdaterServicesContext = React.createContext<UpdaterServicesContextValue | null>(null);

/**
 * Props for UpdaterServicesProvider
 */
export interface UpdaterServicesProviderProps {
  children: React.ReactNode;
  /** Base URL for firmware files (defaults to /recovery/) */
  firmwareBaseUrl?: string;
  /** Optional HID navigator for testing (defaults to browser navigator.hid) */
  hidNavigator?: IHIDNavigator | null;
}

/**
 * Provider component for updater services
 */
export const UpdaterServicesProvider: React.FC<UpdaterServicesProviderProps> = ({
  children,
  firmwareBaseUrl = '/recovery/',
  hidNavigator,
}) => {
  // Track current HID device
  const [hidDevice, setHidDevice] = React.useState<HIDDevice | null>(null);

  // Track firmware state
  const [firmware, setFirmware] = React.useState<FirmwareVersion | null>(null);

  // Create stable service instances
  const loggerRef = React.useRef<UpdaterLoggerService>(new UpdaterLoggerService());

  // Progress callback reference (can be updated by step components)
  const progressCallbackRef = React.useRef<ProgressCallback | undefined>(undefined);

  // Create IMX loader with progress callback
  const imxLoaderRef = React.useRef<IMXLoaderService>(
    new IMXLoaderService(
      loggerRef.current,
      DEVICE_CONFIGS,
      DEVICE_CONFIG_CHAINS,
      (percent, message) => {
        if (progressCallbackRef.current) {
          progressCallbackRef.current(percent, message);
        }
      }
    )
  );

  // Create connection manager (with optional HID navigator for testing)
  const connectionManagerRef = React.useRef<DeviceConnectionManager>(
    new DeviceConnectionManager(loggerRef.current, imxLoaderRef.current, hidNavigator)
  );

  // Create firmware download service
  const firmwareServiceRef = React.useRef<FirmwareDownloadService>(
    new FirmwareDownloadService(loggerRef.current)
  );

  /**
   * Fetch firmware from cloud storage
   */
  const fetchFirmware = React.useCallback(async (version?: string): Promise<FirmwareVersion> => {
    const result = await firmwareServiceRef.current.getFirmware(version);
    setFirmware(result);
    return result;
  }, []);

  /**
   * Load a binary file - uses signed URL from firmware service if available,
   * otherwise falls back to firmware base URL
   */
  const loadFile = React.useCallback(
    async (filename: string): Promise<Uint8Array> => {
      let url: string;

      // Try to get signed URL from firmware service first
      if (firmwareServiceRef.current.isLoaded) {
        try {
          url = firmwareServiceRef.current.getFileUrl(filename);
          loggerRef.current.info(`Loading file from signed URL: ${filename}`);
        } catch {
          // File not in firmware manifest, fall back to base URL
          url = `${firmwareBaseUrl}${filename}`;
          loggerRef.current.info(`Loading file from base URL: ${url}`);
        }
      } else {
        url = `${firmwareBaseUrl}${filename}`;
        loggerRef.current.info(`Loading file: ${url}`);
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${filename}: ${response.status} ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);
        loggerRef.current.success(`Loaded ${filename}: ${data.length} bytes`);
        return data;
      } catch (error) {
        loggerRef.current.error(`Failed to load ${filename}`, error);
        throw error;
      }
    },
    [firmwareBaseUrl]
  );

  /**
   * Set the progress callback
   */
  const setProgressCallback = React.useCallback((callback: ProgressCallback | undefined) => {
    progressCallbackRef.current = callback;
  }, []);

  /**
   * Disconnect and cleanup the HID device
   */
  const disconnectDevice = React.useCallback(async () => {
    if (!hidDevice) return;

    try {
      // Close the device connection
      if (hidDevice.opened) {
        loggerRef.current.info('Closing HID device connection');
        await hidDevice.close();
      }

      // Forget the device to clear Chrome's permission cache (Chrome 100+)
      if ('forget' in hidDevice && typeof (hidDevice as any).forget === 'function') {
        loggerRef.current.info('Forgetting device to clear permission cache');
        await (hidDevice as any).forget();
      }

      loggerRef.current.success('Device disconnected');
    } catch (err) {
      // Device may already be disconnected, which is fine
      loggerRef.current.debug('Device disconnect error (may already be disconnected)', err);
    } finally {
      setHidDevice(null);
    }
  }, [hidDevice]);

  // Create context value
  const contextValue = React.useMemo<UpdaterServicesContextValue>(
    () => ({
      logger: loggerRef.current,
      imxLoader: imxLoaderRef.current,
      connectionManager: connectionManagerRef.current,
      firmwareService: firmwareServiceRef.current,
      hidDevice,
      setHidDevice,
      loadFile,
      setProgressCallback,
      firmware,
      fetchFirmware,
      disconnectDevice,
    }),
    [hidDevice, loadFile, setProgressCallback, firmware, fetchFirmware, disconnectDevice]
  );

  return (
    <UpdaterServicesContext.Provider value={contextValue}>
      {children}
    </UpdaterServicesContext.Provider>
  );
};

/**
 * Hook to access updater services
 */
export function useUpdaterServices(): UpdaterServicesContextValue {
  const context = React.useContext(UpdaterServicesContext);

  if (!context) {
    throw new Error('useUpdaterServices must be used within an UpdaterServicesProvider');
  }

  return context;
}

/**
 * Hook to access just the logger
 */
export function useUpdaterLogger(): UpdaterLoggerService {
  const { logger } = useUpdaterServices();
  return logger;
}
